import React from 'react';
import { SanityProtein } from 'src/types/sanity/types.sanity.product';
import { Typography, Box, Stack, useTheme } from '@mui/material';

interface ProteinProps {
  protein: SanityProtein[];
  get: string | null;
  set: (value: string | null) => void;
}
const Protein = ({ protein, get, set }: ProteinProps) => {

  const theme = useTheme();

  if (!protein) return null;

  const handleSelect = (value: string) => {
    set(value);
  };

  const cow = (
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="15" viewBox="0 0 22 15" fill="none">
      <path
        d="M21.5019 14.5299L22 5.06637C22 3.35454 20.86 1.9552 18.9421 1.99115L9.15323 2.12942C9.12926 2.12942 9.10529 2.12942 9.08131 2.12389L6.21789 1.63993C6.0048 1.60398 5.85031 1.41316 5.85031 1.18916V0.871128C5.85031 0.503319 5.45343 0.287611 5.16043 0.495022L5.13379 0.514381C4.97397 0.630531 4.75821 0.622234 4.60639 0.492257L4.14824 0.102323C4.07366 0.0359513 3.9751 0 3.87388 0H3.10942C2.66459 0 2.50211 0.608407 2.88301 0.846239C3.14404 1.0094 3.17068 1.39381 2.93362 1.59292L0.163423 3.91869C-0.0123775 4.06803 -0.0523322 4.33075 0.0728592 4.5271L0.906581 5.82411C1.00514 5.97622 1.17827 6.05365 1.35141 6.02046L4.51849 5.41759C4.68363 5.38717 4.85144 5.4563 4.95266 5.59458L8.13572 10.0747C8.18366 10.141 8.21296 10.2212 8.21829 10.3014L8.49797 13.3435C8.50597 13.4375 8.48732 13.5343 8.44204 13.6145L8.05847 14.3031C7.888 14.6073 8.10109 14.9889 8.43937 14.9889H8.83093C9.04402 14.9889 9.22515 14.8313 9.26244 14.6156L9.71526 12.0271C9.79251 11.5929 10.3572 11.5127 10.5437 11.911L11.0977 13.0973C11.1456 13.1997 11.1536 13.3158 11.1243 13.4264L10.8446 14.4054C10.7621 14.6986 10.9725 14.9917 11.2655 14.9917H11.6944C11.9447 14.9917 12.1445 14.776 12.1339 14.516L11.9554 10.2406C11.9474 10.0221 12.0886 9.82854 12.291 9.77876L15.3942 9.00442C15.5913 8.95465 15.7937 9.05144 15.8869 9.23949L16.8911 11.2583C16.9258 11.3302 16.9444 11.4104 16.9391 11.4906L16.8751 12.9038C16.8725 12.984 16.8485 13.0614 16.8059 13.1278L16.0867 14.2948C15.9002 14.599 16.1107 14.9972 16.457 14.9972H16.7366C16.9098 14.9972 17.0669 14.8921 17.1388 14.7262L18.753 10.9375C18.8915 10.6112 19.3257 10.5697 19.5175 10.8656L20.4391 12.2788C20.4897 12.3534 20.5137 12.4447 20.5137 12.536V13.9436C20.5137 14.0155 20.4977 14.0846 20.4684 14.1482L20.3778 14.339C20.2313 14.6433 20.4444 15 20.7721 15H21.0651C21.3075 15 21.5046 14.7954 21.5046 14.5437L21.5019 14.5299Z"
        fill="#1A1919"
      />
    </svg>
  );

  const plant = (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="14" viewBox="0 0 18 14" fill="none">
      <path
        d="M0 10.4763C0 10.4763 0.472554 3.42602 6.12668 1.16678C11.7808 -1.09247 18 0.624936 18 0.624936C18 0.624936 17.6233 8.84758 12.911 11.5599C8.19864 14.2723 3.58216 14.0917 1.31852 13.9079C1.31852 13.9079 3.22526 7.19038 10.5548 4.5097C10.5548 4.5097 4.14724 4.41781 0 10.4731V10.4763Z"
        fill="#9E9E9E"
      />
    </svg>
  );

  const getIcon = (protein: string) => {
    switch (protein) {
      case 'protein_whey':
        return cow;
      case 'protein_pea':
        return plant;
      default:
        return cow;
    }
  };

  return (
    <>
      <Stack spacing={2}>
        <Typography variant={'h6'}>CHOOSE PROTEIN</Typography>

        <Box display={'flex'} gap={2}>
          {protein.map((protein, index) => {
            const selected = get === protein.proteinType;
            return (
              <>
                <Stack spacing={1} width={'100%'}>
                  <Box
                    display={'flex'}                    
                    flexShrink={1}                    
                    gap={2}
                    justifyContent={'space-between'}
                    key={index}
                    onClick={() => handleSelect(protein.proteinType)}
                    sx={{
                      padding: 1,                                            
                      border: selected ? `1px solid ${theme.palette.primary.main}` : '1px solid grey',
                      borderRadius: '5px',                      
                      bgcolor: selected ? 'primary.light' : '#EEEFE6',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      minHeight: 50, // Ensuring minimum height for consistency
                      '&:hover': {                        
                        bgcolor: 'primary.light',
                      },
                    }}
                  >
                    <Box>{getIcon(protein.proteinType)}</Box>
                    <Box width={'100%'}>
                      <Typography variant="body2">{protein.name}</Typography>
                    </Box>
                  </Box>
                  <Typography variant={'caption'}>{protein.description}</Typography>
                </Stack>
              </>
            );
          })}
        </Box>
      </Stack>
    </>
  );
};

export default Protein;
